<template>
  <div class="explore-wrapper">
    <!-- Widget Cloudflare Turnstile (oculto pero funcional) -->
    <div ref="turnstile" id="turnstile-explore" style="position:fixed; top:-9999px; left:-9999px; opacity:0; pointer-events:none; z-index:-1; width:300px; height:65px; overflow:hidden;"></div>

    <!-- Loader inicial -->
    <div v-if="loading && filteredHarmonys.length === 0" class="explore-loader">
      <ExploreLoader />
    </div>

    <!-- Contenedor principal tipo TikTok -->
    <div
      v-else
      class="explore-container"
      ref="exploreContainer"
      @wheel="handleWheel"
      @scroll="handleScroll"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
    >
      <!-- Slides verticales: filtrados sin duplicados consecutivos -->
      <div
        v-for="(harmony, index) in filteredHarmonys"
        :key="harmony._uniqueKey"
        class="explore-slide"
        :data-index="index"
      >
        <!-- Fondo: video o imagen del Harmony (featured_media aleatoria) -->
        <div class="slide-background">
          <video
            v-if="getFeaturedMedia(harmony).type === 'video'"
            class="bg-video"
            :src="getFeaturedMedia(harmony).path"
            loop
            playsinline
            muted
            autoplay
            preload="auto"
            @loadedmetadata="handleVideoLoaded"
          />
          <template v-else-if="getFeaturedMedia(harmony).type === 'image'">
            <!-- Fondo blur de la misma imagen para evitar bordes negros -->
            <img
              class="bg-image-blur"
              :src="getFeaturedMedia(harmony).path"
              alt=""
              loading="eager"
              decoding="async"
            />
            <img
              class="bg-image"
              :src="getFeaturedMedia(harmony).path"
              alt="background"
              loading="eager"
              decoding="async"
            />
          </template>
          <div
            v-else
            class="bg-color"
            :style="{ background: getFeaturedMedia(harmony).color || '#1a1a2e' }"
          />
        </div>

        <!-- Overlay con difuminado -->
        <div class="slide-overlay">
          <!-- Top bar: Logo + Flechas -->
          <div class="top-bar">
            <div class="logo-area" @click="goHome">
              <img
                :src="harmonyBrandLogo"
                class="brand-logo-img"
                alt="Harmony"
              />
            </div>
            <div class="nav-arrows">
              <div
                class="arrow-btn"
                :class="{ disabled: currentIndex <= 0 }"
                @click="prevSlide"
              >
                <v-icon class="arrow-icon" color="white" size="24">mdi-arrow-up-thin</v-icon>
              </div>
              <div
                class="arrow-btn"
                :class="{ bounce: showArrowHint && index === 0 }"
                @click="nextSlide"
              >
                <v-icon class="arrow-icon" color="white" size="24">mdi-arrow-down-thin</v-icon>
              </div>
            </div>
          </div>

          <!-- Bottom content -->
          <div class="bottom-content">
            <!-- Left: Nombre + Descripcion + Boton -->
            <div class="left-info">
              <h2 class="harmony-name">{{ harmony.name || $t('explore.noHarmony') }}</h2>
              <p v-if="harmony.description" class="harmony-desc">
                {{ harmony.description }}
              </p>
              <div class="visit-btn" @click="visitHarmony(harmony.slug)">
                {{ $t('explore.visit') }}
              </div>
            </div>

            <!-- Right: Acciones -->
            <div class="right-actions">
              <SidebarActions
                :logoPath="harmony.logo_path"
                :userName="harmony.name"
                :likes="harmony.likes_count"
                :isLiked="harmony.is_liked"
                :editor="editor"
                :muted="muted"
                :mediaViews="harmony.media_views_formatted"
                @like="handleLike(harmony.id)"
                @share="handleShare(harmony.slug)"
                @toggle-volume="handleVolume"
              />
            </div>
          </div>

          <!-- Barra de progreso 10s -->
          <div class="progress-bar-container">
            <div
              class="progress-bar"
              :style="currentIndex === index ? { width: progress + '%' } : { width: '0%' }"
            ></div>
          </div>
        </div>
      </div>

      <!-- Loader de mas contenido -->
      <div v-if="loading && filteredHarmonys.length > 0" class="explore-loader-more">
        <ExploreLoader />
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axios.js';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { loadTurnstile } from '@/utils/turnstileLoader';
import SidebarActions from "./SidebarActions.vue";
import ExploreLoader from "./ExploreLoader.vue";
import HarmonyLogo from "@/assets/logo/1-06h.png";

export default {
  name: "Explore",
  components: {
    SidebarActions,
    ExploreLoader,
  },
  props: {
    harmonys: {
      type: Array,
      default: () => [],
    },
    editor: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    hasMore: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      harmonyBrandLogo: HarmonyLogo,
      showArrowHint: true,
      muted: true,
      touchStartY: 0,
      currentIndex: 0,
      progress: 0,
      progressInterval: null,
      autoAdvanceTimer: null,
      lastWheelTime: 0,
      slideHeight: 0,
      viewedMediaIds: new Set(),
      viewDebounceTimer: null,
      scrollDebounceTimer: null,
      isScrolling: false,
      fpPromise: null,
      fpVisitorId: null,
      fpComponents: null,
      widgetId: null,
    };
  },
  computed: {
    filteredHarmonys() {
      const result = [];
      let lastKey = null;

      this.harmonys.forEach((h, idx) => {
        const path = h?.site_setting?.featured_media?.path || '';
        const key = `${h.id}-${path}`;

        // Saltar duplicados consecutivos (mismo harmony + misma imagen)
        if (key === lastKey) return;

        result.push({
          ...h,
          _uniqueKey: `${h.id}-${idx}-${path}`,
          _originalIndex: idx,
        });
        lastKey = key;
      });

      return result;
    },
  },
  watch: {
    harmonys(newVal, oldVal) {
      const isFirstLoad = oldVal.length === 0 && newVal.length > 0;
      if (isFirstLoad) {
        this.currentIndex = 0;
        this.resetAutoAdvance();
        this.$nextTick(() => {
          this.scrollToCurrent();
          this.updateSlideHeight();
          this.manageVideoPlayback();
        });
        return;
      }

      // Ajustar índice si quedó fuera de rango tras filtrar duplicados o cargar más datos
      if (this.currentIndex >= this.filteredHarmonys.length) {
        this.currentIndex = Math.max(0, this.filteredHarmonys.length - 1);
        this.scrollToCurrent();
      }

      this.$nextTick(() => {
        this.updateSlideHeight();
        this.manageVideoPlayback();
        this.resetAutoAdvance();
      });
    },
    currentIndex(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.resetAutoAdvance();
        this.$nextTick(() => {
          this.manageVideoPlayback();
        });
        // Debounce: solo registrar vista si el slide permanece activo >= 600ms
        clearTimeout(this.viewDebounceTimer);
        this.viewDebounceTimer = setTimeout(() => {
          if (this.currentIndex === newVal) {
            this.registerCurrentMediaView();
          }
        }, 600);
      }
      // Precargar adelantado cuando faltan 3 slides para el final
      const remaining = this.filteredHarmonys.length - newVal - 1;
      if (remaining <= 3 && !this.loading) {
        this.$emit("load-more");
      }
    },
  },
  mounted() {
    this.updateSlideHeight();
    this.startAutoAdvance();
    setTimeout(() => {
      this.showArrowHint = false;
    }, 7000);
    this.$nextTick(() => {
      this.manageVideoPlayback();
    });
    window.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('resize', this.updateSlideHeight);

    // Inicializar FingerprintJS UNA SOLA VEZ
    const savedVisitorId = localStorage.getItem('visitorId');
    if (savedVisitorId) {
      this.fpVisitorId = savedVisitorId;
    }

    this.fpPromise = FingerprintJS.load().then((fp) => fp.get()).then((result) => {
      this.fpVisitorId = result.visitorId;
      this.fpComponents = result.components;
      localStorage.setItem('visitorId', result.visitorId);
      return result;
    });

    // El token de Cloudflare se solicitará bajo demanda en registerCurrentMediaView
    // para obtener un token fresco cada vez que se scrollea a un nuevo slide
  },
  beforeDestroy() {
    this.clearTimers();
    clearTimeout(this.viewDebounceTimer);
    window.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('resize', this.updateSlideHeight);
    // Limpiar widget de Turnstile si existe para evitar duplicados
    if (this.widgetId && window.turnstile && typeof window.turnstile.remove === 'function') {
      try {
        window.turnstile.remove(this.widgetId);
        this.widgetId = null;
      } catch (e) {
        // Silenciar error si el widget ya no existe
      }
    }
  },
  methods: {
    clearTimers() {
      if (this.progressInterval) {
        clearInterval(this.progressInterval);
        this.progressInterval = null;
      }
      if (this.autoAdvanceTimer) {
        clearTimeout(this.autoAdvanceTimer);
        this.autoAdvanceTimer = null;
      }
    },
    startAutoAdvance() {
      this.clearTimers();
      this.progress = 0;
      this.progressInterval = setInterval(() => {
        this.progress += 1;
        if (this.progress >= 100) {
          this.progress = 100;
          clearInterval(this.progressInterval);
          this.progressInterval = null;
        }
      }, 100);

      this.autoAdvanceTimer = setTimeout(() => {
        this.nextSlide();
      }, 10000);
    },
    resetAutoAdvance() {
      this.clearTimers();
      this.progress = 0;
      this.startAutoAdvance();
    },
    updateSlideHeight() {
      const container = this.$refs.exploreContainer;
      if (!container) return;
      const firstSlide = container.querySelector('.explore-slide');
      if (firstSlide) {
        this.slideHeight = firstSlide.offsetHeight;
      }
    },
    getFeaturedMedia(harmony) {
      const settings = harmony.site_setting || {};
      const media = settings.featured_media;
      const page = settings.page || {};

      if (media) {
        return {
          type: media.type,
          path: media.path,
          media_id: media.media_id || null,
          color: null,
          views: media.views || 0,
          views_formatted: media.views_formatted || '0',
        };
      }

      return {
        type: null,
        path: null,
        media_id: null,
        color: page.color || "#1a1a2e",
      };
    },
    async registerCurrentMediaView() {
      const harmony = this.filteredHarmonys[this.currentIndex];
      if (!harmony) {
        return;
      }

      const media = this.getFeaturedMedia(harmony);
      if (!media.media_id) {
        return;
      }

      // Deduplicacion cliente: registrar cada media solo una vez por sesion
      const viewKey = `${harmony.id}:${media.media_id}`;
      if (this.viewedMediaIds.has(viewKey)) {
        return;
      }
      this.viewedMediaIds.add(viewKey);

      // Obtener token de Cloudflare fresco para cada vista
      let token = null;
      try {
        token = await this.getTokenCloudflare();
      } catch (e) {
        // Si no se puede obtener token, enviar null igualmente
      }

      // Construir payload con datos disponibles ahora (sin esperar)
      const payload = this.buildMediaViewPayload(media.media_id, harmony.id, token);

      // Enviar inmediatamente sin bloquear la navegacion
      // No se hace optimistic update: los contadores se refrescan desde el backend
      // cuando se trae mas contenido via load-more (ver padre Explore.vue y getExploreHarmonys).
      axios.post('/register-media-view', payload).catch(() => {
        // Silenciar errores de registro de vista y revertir dedup para reintentar luego
        this.viewedMediaIds.delete(viewKey);
      });
    },
    buildMediaViewPayload(mediaId, siteId, token = null) {
      const payload = {
        media_id: mediaId,
        site_id: siteId,
        is_explore: true,
        fingerprint: this.fpVisitorId,
        language: navigator.language || null,
        timezone: null,
        platform: navigator.platform || null,
        screen_resolution: (screen.width + 'x' + screen.height) || null,
        webgl_vendor: null,
        audio_hash: null,
        referrer: document.referrer || window.location.href || null,
        token_cloudflare: token,
      };

      try {
        payload.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
      } catch (e) {
        // noop
      }

      // Extraer webgl_vendor manualmente (no siempre disponible en FingerprintJS open source)
      try {
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        if (gl) {
          const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
          if (debugInfo) {
            payload.webgl_vendor = {
              vendor: gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL),
              renderer: gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL),
            };
          }
        }
      } catch (e) {
        // Silenciar
      }

      // Extraer audio_hash desde FingerprintJS si esta disponible
      if (this.fpComponents) {
        try {
          const c = this.fpComponents;
          if (c.audio && c.audio.value !== undefined) {
            payload.audio_hash = String(c.audio.value);
          }
        } catch (e) {
          // Silenciar
        }
      }

      return payload;
    },
    handleLike(harmonyId) {
      this.$emit("like-site", harmonyId);
    },
    handleShare(harmonySlug) {
      this.$emit("share-site", harmonySlug);
    },
    handleVolume(muted) {
      this.muted = muted;
      this.manageVideoPlayback();
    },
    handleVideoLoaded(event) {
      const video = event.target;
      const slide = video.closest('.explore-slide');
      if (!slide) return;

      const slideIndex = parseInt(slide.dataset.index, 10);
      if (slideIndex !== this.currentIndex) {
        video.pause();
        video.currentTime = 0;
        return;
      }

      video.muted = this.muted;
      video.play().catch(() => {});
    },
    visitHarmony(slug) {
      if (this.editor) return;
      if (slug) {
        window.open(`/${slug}`, '_blank');
      }
    },
    goHome() {
      window.location.href = '/';
    },
    prevSlide() {
      if (this.currentIndex <= 0) return;
      this.currentIndex--;
      this.scrollToCurrent();
      this.resetAutoAdvance();
    },
    nextSlide() {
      if (this.currentIndex >= this.filteredHarmonys.length - 1) {
        if (!this.loading) {
          this.$emit("load-more");
        }
        return;
      }
      this.currentIndex++;
      this.scrollToCurrent();
      this.resetAutoAdvance();
    },
    scrollToCurrent() {
      const container = this.$refs.exploreContainer;
      if (!container) return;
      const slide = container.querySelector(`.explore-slide[data-index="${this.currentIndex}"]`);
      if (slide) {
        this.isScrolling = true;
        container.scrollTo({
          top: slide.offsetTop,
          behavior: 'smooth',
        });
        // Liberar el flag cuando el scroll programático termine (~350ms para smooth)
        setTimeout(() => {
          this.isScrolling = false;
        }, 400);
      }
    },
    handleScroll() {
      // Ignorar eventos de scroll durante el scroll programático (smooth)
      if (this.isScrolling) return;

      const container = this.$refs.exploreContainer;
      if (!container || this.slideHeight === 0) return;

      clearTimeout(this.scrollDebounceTimer);
      this.scrollDebounceTimer = setTimeout(() => {
        const scrollTop = container.scrollTop;
        const newIndex = Math.round(scrollTop / this.slideHeight);

        if (newIndex !== this.currentIndex && newIndex >= 0 && newIndex < this.filteredHarmonys.length) {
          this.currentIndex = newIndex;
          this.resetAutoAdvance();
        }
      }, 150);
    },
    handleWheel(e) {
      e.preventDefault();
      const now = Date.now();
      if (now - this.lastWheelTime < 500) return;
      this.lastWheelTime = now;

      if (e.deltaY > 30) {
        this.nextSlide();
      } else if (e.deltaY < -30) {
        this.prevSlide();
      }
    },
    handleTouchStart(e) {
      this.touchStartY = e.changedTouches[0].screenY;
    },
    handleTouchMove(e) {
      // Prevenir scroll nativo del navegador para evitar conflicto con JS
      e.preventDefault();
    },
    handleTouchEnd(e) {
      const touchEndY = e.changedTouches[0].screenY;
      const diff = this.touchStartY - touchEndY;
      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          this.nextSlide();
        } else {
          this.prevSlide();
        }
      }
    },
    handleKeydown(e) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.nextSlide();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.prevSlide();
      }
    },
    manageVideoPlayback() {
      const container = this.$refs.exploreContainer;
      if (!container) return;

      // Pausar y resetear TODOS los videos del contenedor para evitar audios huérfanos
      const allVideos = container.querySelectorAll('video');
      allVideos.forEach((video) => {
        video.pause();
        video.currentTime = 0;
      });

      // Reproducir únicamente el video del slide actual (usando data-index para evitar desfase por duplicados filtrados)
      const currentSlide = container.querySelector(`.explore-slide[data-index="${this.currentIndex}"]`);
      if (currentSlide) {
        const video = currentSlide.querySelector('video');
        if (video) {
          video.muted = this.muted;
          video.play().catch(() => {});
        }
      }
    },
    formatCount(count) {
      if (count >= 1000000) {
        return (count / 1000000).toFixed(1) + "M";
      }
      if (count >= 1000) {
        return (count / 1000).toFixed(1) + "k";
      }
      return (count || 0).toString();
    },
    async getTokenCloudflare() {
      try {
        const turnstile = await loadTurnstile();
        return new Promise((resolve, reject) => {
          if (this.widgetId && turnstile && typeof turnstile.remove === 'function') {
            try {
              turnstile.remove(this.widgetId);
            } catch (e) {
              // Ignore removal errors
            }
          }
          this.widgetId = turnstile.render(
            this.$refs.turnstile,
            {
              sitekey: '0x4AAAAAABbWOJ_t8X7HYmoE',
              callback: (token) => {
                resolve(token);
              },
              'error-callback': (err) => {
                reject(err);
              }
            }
          );
        });
      } catch (err) {
        console.error('Error loading Turnstile:', err);
        throw err;
      }
    },
  },
};
</script>

<style scoped>
/* Wrapper general */
.explore-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background: #000;
  position: relative;
}

/* Loader */
.explore-loader {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(26, 26, 46, 0.55);
  z-index: 9999;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  margin: auto;
}

.explore-loader-more {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  height: 100dvh;
  width: 100%;
  background: #000;
  flex-shrink: 0;
}

/* Contenedor principal */
.explore-container {
  width: 100%;
  height: 100vh;
  height: 100dvh;
  overflow-y: auto;
  overflow-x: hidden;
  scroll-behavior: auto;
  -webkit-overflow-scrolling: touch;
  touch-action: none;
  position: relative;
  background: #000;
  display: flex;
  flex-direction: column;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.explore-container::-webkit-scrollbar {
  display: none;
}

/* En PC: simular pantalla de movil centrada */
@media (min-width: 768px) {
  .explore-loader,
  .explore-loader-more,
  .explore-container {
    max-width: 480px;
    height: 90vh;
    border-radius: 12px;
    overflow-y: auto;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  }
}

/* Cada slide */
.explore-slide {
  position: relative;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  flex-shrink: 0;
  overflow: hidden;
  background: #000;
}

@media (min-width: 768px) {
  .explore-slide {
    height: 90vh;
  }
}

/* Background */
.slide-background {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bg-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.bg-image-blur {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: blur(20px) brightness(0.6);
  display: block;
  z-index: 1;
}

.bg-image {
  position: relative;
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  z-index: 2;
}

.bg-color {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
}

/* Overlay con difuminado */
.slide-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.5) 0%,
    transparent 25%,
    transparent 50%,
    rgba(0, 0, 0, 0.85) 100%
  );
}

/* Top bar */
.top-bar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 14px 20px 0;
  pointer-events: auto;
}

.logo-area {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.brand-logo-img {
  height: 32px;
  width: auto;
  max-width: 120px;
  object-fit: contain;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.6));
}

/* Flechas de navegacion */
.nav-arrows {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.arrow-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0.8;
  transition: opacity 0.2s ease;
}

.arrow-btn:active {
  opacity: 1;
}

.arrow-btn.disabled {
  opacity: 0.2;
  pointer-events: none;
}

.arrow-btn.bounce {
  animation: bounceArrow 1.2s ease-in-out infinite;
}

@keyframes bounceArrow {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(8px);
  }
}

/* Bottom content */
.bottom-content {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding: 0 20px 20px;
  padding-bottom: max(20px, env(safe-area-inset-bottom));
  pointer-events: auto;
}

/* Extra padding en móvil para evitar que el navegador oculte contenido */
@media (max-width: 767px) {
  .slide-overlay {
    padding-bottom: max(28px, env(safe-area-inset-bottom));
  }
  .bottom-content {
    padding-bottom: 0;
  }
}

.left-info {
  flex: 1;
  padding-right: 16px;
  padding-bottom: 4px;
  text-align: left;
}

.harmony-name {
  color: white;
  font-weight: 700;
  font-size: 15px;
  margin: 0 0 4px;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.7);
  line-height: 1.3;
  text-align: left;
}

.harmony-desc {
  color: rgba(255, 255, 255, 0.85);
  font-size: 12px;
  margin: 0 0 4px;
  line-height: 1.4;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-align: left;
}

.visit-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 14px;
  margin-top: 6px;
  border-radius: 20px;
  background: linear-gradient(135deg, #2a0a2e 0%, #4a1a4e 100%);
  color: #d4a8d8;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  pointer-events: auto;
}

.visit-btn:active {
  transform: scale(0.95);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
}

/* Barra de progreso */
.progress-bar-container {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: rgba(255, 255, 255, 0.15);
  z-index: 3;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #4a1a4e, #8e84c0);
  transition: width 0.1s linear;
}

.right-actions {
  flex-shrink: 0;
  padding-bottom: 4px;
}
</style>
